import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import RichTextEditor from '../components/RichTextEditor';
import { clientiAPI, serviziAPI, preventiviAPI, modalitaPagamentoAPI, specialistiAPI, impostazioniAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Euro,
  Plus,
  Trash2,
  Save,
  Users,
  Briefcase,
  GripVertical,
  Clock,
  Package,
} from 'lucide-react';

const NuovoPreventivo = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Data
  const [clienti, setClienti] = useState([]);
  const [servizi, setServizi] = useState([]);
  const [modalitaPagamentoOptions, setModalitaPagamentoOptions] = useState([]);
  const [specialistiOptions, setSpecialistiOptions] = useState([]);
  
  // Form data
  const [selectedCliente, setSelectedCliente] = useState('');
  const [data, setData] = useState(new Date().toISOString().split('T')[0]);
  const [richiestaCliente, setRichiestaCliente] = useState(`In seguito alla riunione del ${new Date().toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })} e ai successivi scambi di email e messaggi, il cliente ha espresso il desiderio di effettuare un restyling tecnico e seo del proprio sito internet https://www.XXXXXX.it.

Il cliente ha espresso la volontà di ricevere un preventivo dettagliato per la realizzazione del sito web, includendo i costi di sviluppo, i tempi di consegna e le modalità.`);
  const [selectedServizi, setSelectedServizi] = useState([]);
  const [note, setNote] = useState('');
  const [modalitaPagamento, setModalitaPagamento] = useState('');
  const [validitaGiorni, setValiditaGiorni] = useState(15);
  const [selectedSpecialisti, setSelectedSpecialisti] = useState([]);
  const [tempiRealizzazione, setTempiRealizzazione] = useState('');
  const [materialeCliente, setMaterialeCliente] = useState('');
  const [impostazioniPreventivo, setImpostazioniPreventivo] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientiRes, serviziRes, modalitaRes, specialistiRes, impostazioniRes] = await Promise.all([
        clientiAPI.list(),
        serviziAPI.list(),
        modalitaPagamentoAPI.list(),
        specialistiAPI.list(),
        impostazioniAPI.getPreventivo(),
      ]);
      setClienti(clientiRes.data);
      setServizi(serviziRes.data);
      setModalitaPagamentoOptions(modalitaRes.data);
      setSpecialistiOptions(specialistiRes.data);
      
      // Set default values from impostazioni
      const impostazioni = impostazioniRes.data;
      setImpostazioniPreventivo(impostazioni);
      if (impostazioni.tempi_realizzazione) {
        setTempiRealizzazione(impostazioni.tempi_realizzazione);
      }
      if (impostazioni.materiale_cliente) {
        setMaterialeCliente(impostazioni.materiale_cliente);
      }
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleServizio = (servizio) => {
    const exists = selectedServizi.find(s => s.servizio_id === servizio.id);
    if (exists) {
      setSelectedServizi(selectedServizi.filter(s => s.servizio_id !== servizio.id));
    } else {
      setSelectedServizi([
        ...selectedServizi,
        {
          servizio_id: servizio.id,
          nome: servizio.nome,
          descrizione: servizio.descrizione,
          attivita_previste: servizio.attivita_previste_default || '',
          prezzo_listino: servizio.prezzo_listino,
          prezzo_scontato: servizio.prezzo_listino,
          tipo_prezzo: 'una_tantum',
          importo_mensile: null,
          documento_id: servizio.documento_id || null,
        },
      ]);
    }
  };

  const handleUpdateServizio = (servizioId, field, value) => {
    setSelectedServizi(
      selectedServizi.map(s =>
        s.servizio_id === servizioId ? { ...s, [field]: value } : s
      )
    );
  };

  const totaleListino = selectedServizi.reduce((sum, s) => sum + (parseFloat(s.prezzo_listino) || 0), 0);
  const totaleScontato = selectedServizi.reduce((sum, s) => sum + (parseFloat(s.prezzo_scontato) || 0), 0);
  const sconto = totaleListino - totaleScontato;

  const handleSubmit = async () => {
    if (!selectedCliente) {
      toast.error('Seleziona un cliente');
      return;
    }
    if (selectedServizi.length === 0) {
      toast.error('Seleziona almeno un servizio');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        cliente_id: selectedCliente,
        data,
        servizi: selectedServizi.map(s => ({
          ...s,
          prezzo_listino: parseFloat(s.prezzo_listino) || 0,
          prezzo_scontato: parseFloat(s.prezzo_scontato) || 0,
          tipo_prezzo: s.tipo_prezzo || 'una_tantum',
          importo_mensile: s.tipo_prezzo === 'rateizzato' ? parseFloat(s.importo_mensile) || 0 : null,
        })),
        note,
        modalita_pagamento: modalitaPagamento,
        validita_giorni: parseInt(validitaGiorni) || 15,
        richiesta_cliente: richiestaCliente,
        specialisti_ids: selectedSpecialisti,
        tempi_realizzazione: tempiRealizzazione,
        materiale_cliente: materialeCliente,
      };

      const response = await preventiviAPI.create(payload);
      toast.success('Preventivo creato con successo!');
      navigate(`/preventivi/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Strip HTML for preview
  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="nuovo-preventivo-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/preventivi')}
            className="h-10 w-10 p-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Nuovo Preventivo</h1>
            <p className="text-slate-500 mt-1">Compila i dati per creare un nuovo preventivo</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full font-medium transition-colors ${
                  step >= s
                    ? 'bg-orange-500 text-white'
                    : 'bg-slate-100 text-slate-400'
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div
                  className={`w-24 h-1 rounded ${
                    step > s ? 'bg-orange-500' : 'bg-slate-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="flex justify-center gap-16 text-sm text-slate-500">
          <span className={step === 1 ? 'font-medium text-slate-900' : ''}>Cliente</span>
          <span className={step === 2 ? 'font-medium text-slate-900' : ''}>Servizi</span>
          <span className={step === 3 ? 'font-medium text-slate-900' : ''}>Riepilogo</span>
        </div>

        {/* Step 1: Cliente Selection */}
        {step === 1 && (
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading">Seleziona Cliente</CardTitle>
              <CardDescription>
                Scegli il cliente per il preventivo e la data
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {clienti.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">Nessun cliente presente</p>
                  <Button variant="outline" onClick={() => navigate('/clienti')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Aggiungi Cliente
                  </Button>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Cliente *</Label>
                    <Select value={selectedCliente} onValueChange={setSelectedCliente}>
                      <SelectTrigger data-testid="select-cliente">
                        <SelectValue placeholder="Seleziona un cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clienti.map((cliente) => (
                          <SelectItem key={cliente.id} value={cliente.id}>
                            {cliente.ragione_sociale}
                            {cliente.partita_iva && ` (P.IVA: ${cliente.partita_iva})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Data Preventivo *</Label>
                    <Input
                      type="date"
                      value={data}
                      onChange={(e) => setData(e.target.value)}
                      data-testid="preventivo-data"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Richiesta del Cliente</Label>
                    <textarea
                      className="w-full min-h-[150px] p-3 border border-slate-200 rounded-lg text-sm resize-y focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      value={richiestaCliente}
                      onChange={(e) => setRichiestaCliente(e.target.value)}
                      placeholder="Descrivi la richiesta del cliente..."
                      data-testid="richiesta-cliente"
                    />
                    <p className="text-xs text-slate-500">
                      Segnaposto nel documento: {'{{RICHIESTA_CLIENTE}}'}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Servizi Selection */}
        {step === 2 && (
          <div className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading">Seleziona Servizi</CardTitle>
                <CardDescription>
                  Scegli i servizi da includere nel preventivo
                </CardDescription>
              </CardHeader>
              <CardContent>
                {servizi.length === 0 ? (
                  <div className="text-center py-8">
                    <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">Nessun servizio configurato</p>
                    <Button variant="outline" onClick={() => navigate('/servizi')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Configura Servizi
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {servizi.map((servizio) => {
                      const isSelected = selectedServizi.some(s => s.servizio_id === servizio.id);
                      return (
                        <div
                          key={servizio.id}
                          className={`flex items-start gap-4 p-4 rounded-lg border transition-colors cursor-pointer ${
                            isSelected
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => handleToggleServizio(servizio)}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleToggleServizio(servizio)}
                            data-testid={`servizio-checkbox-${servizio.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900">{servizio.nome}</p>
                            <p className="text-sm text-slate-500 line-clamp-2 mt-1">
                              {stripHtml(servizio.descrizione)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-slate-900">
                              {formatCurrency(servizio.prezzo_listino)}
                            </p>
                            <p className="text-xs text-slate-500">+ IVA</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Selected Services Details */}
            {selectedServizi.length > 0 && (
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading">Dettagli Servizi Selezionati</CardTitle>
                  <CardDescription>
                    Personalizza attività, prezzi e ordine di apparizione. Usa le frecce per riordinare.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {selectedServizi.map((servizio, index) => (
                    <div
                      key={servizio.servizio_id}
                      className="p-4 border border-slate-200 rounded-lg space-y-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                            <span className="text-sm font-bold text-orange-600">{index + 1}</span>
                          </div>
                          <h4 className="font-semibold text-slate-900">{servizio.nome}</h4>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleServizio({ id: servizio.servizio_id })}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Dettaglio della configurazione del servizio</Label>
                        <RichTextEditor
                          key={`editor-${servizio.servizio_id}-${index}`}
                          value={servizio.attivita_previste}
                          onChange={(value) =>
                            handleUpdateServizio(servizio.servizio_id, 'attivita_previste', value)
                          }
                          placeholder="Descrivi la configurazione specifica del servizio per questo cliente..."
                          height="150px"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 pt-2">
                        <div className="space-y-2">
                          <Label>Prezzo Listino</Label>
                          <div className="relative">
                            <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={servizio.prezzo_listino}
                              onChange={(e) =>
                                handleUpdateServizio(servizio.servizio_id, 'prezzo_listino', e.target.value)
                              }
                              className="pl-10"
                              data-testid={`prezzo-listino-${servizio.servizio_id}`}
                            />
                          </div>
                          <p className="text-xs text-slate-500">Modifica il prezzo di listino per questo preventivo</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Prezzo Scontato</Label>
                          <div className="relative">
                            <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={servizio.prezzo_scontato}
                              onChange={(e) =>
                                handleUpdateServizio(servizio.servizio_id, 'prezzo_scontato', e.target.value)
                              }
                              className="pl-10"
                              data-testid={`prezzo-scontato-${servizio.servizio_id}`}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Ricorrenza */}
                      <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                        <div className="space-y-2">
                          <Label>Ricorrenza</Label>
                          <Select
                            value={servizio.tipo_prezzo || 'una_tantum'}
                            onValueChange={(value) =>
                              handleUpdateServizio(servizio.servizio_id, 'tipo_prezzo', value)
                            }
                          >
                            <SelectTrigger data-testid={`tipo-prezzo-${servizio.servizio_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="una_tantum">Una tantum</SelectItem>
                              <SelectItem value="canone_annuale">Canone Annuale</SelectItem>
                              <SelectItem value="canone_mensile">Canone Mensile</SelectItem>
                              <SelectItem value="rateizzato">Costo totale suddiviso in 12 mensilità</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {servizio.tipo_prezzo === 'rateizzato' && (
                          <div className="space-y-2">
                            <Label>Importo Mensilità</Label>
                            <div className="relative">
                              <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={servizio.importo_mensile || ''}
                                onChange={(e) =>
                                  handleUpdateServizio(servizio.servizio_id, 'importo_mensile', e.target.value)
                                }
                                placeholder="Es: 100.00"
                                className="pl-10"
                                data-testid={`importo-mensile-${servizio.servizio_id}`}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Step 3: Riepilogo */}
        {step === 3 && (
          <div className="space-y-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading">Riepilogo Preventivo</CardTitle>
                <CardDescription>
                  Verifica i dati e completa le informazioni aggiuntive
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Cliente Info */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">Cliente</p>
                  <p className="font-semibold text-slate-900">
                    {clienti.find(c => c.id === selectedCliente)?.ragione_sociale}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">Data: {data}</p>
                </div>

                {/* Services Summary */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-900">Servizi</h4>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3 text-sm font-semibold text-slate-600">Servizio</th>
                          <th className="text-right p-3 text-sm font-semibold text-slate-600">Listino</th>
                          <th className="text-right p-3 text-sm font-semibold text-slate-600">Scontato</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedServizi.map((s) => (
                          <tr key={s.servizio_id} className="border-t border-slate-100">
                            <td className="p-3 text-sm">{s.nome}</td>
                            <td className="p-3 text-sm text-right">{formatCurrency(parseFloat(s.prezzo_listino) || 0)}</td>
                            <td className="p-3 text-sm text-right font-medium">
                              {formatCurrency(parseFloat(s.prezzo_scontato) || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-50 border-t border-slate-200">
                        <tr>
                          <td className="p-3 text-sm font-semibold">Totale</td>
                          <td className="p-3 text-sm text-right">{formatCurrency(totaleListino)}</td>
                          <td className="p-3 text-sm text-right font-bold text-lg">
                            {formatCurrency(totaleScontato)}
                          </td>
                        </tr>
                        {sconto > 0 && (
                          <tr>
                            <td colSpan="2" className="p-3 text-sm text-emerald-600">Sconto applicato</td>
                            <td className="p-3 text-sm text-right text-emerald-600 font-medium">
                              -{formatCurrency(sconto)}
                            </td>
                          </tr>
                        )}
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Modalità di Pagamento</Label>
                    {modalitaPagamentoOptions.length > 0 ? (
                      <Select value={modalitaPagamento} onValueChange={setModalitaPagamento}>
                        <SelectTrigger data-testid="modalita-pagamento">
                          <SelectValue placeholder="Seleziona modalità..." />
                        </SelectTrigger>
                        <SelectContent>
                          {modalitaPagamentoOptions.map((m) => (
                            <SelectItem key={m.id} value={m.descrizione || m.nome}>
                              {m.nome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="text-sm text-slate-500 p-3 bg-slate-50 rounded-lg border border-dashed">
                        Nessuna modalità configurata. Vai su Impostazioni per aggiungerle.
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Validità Preventivo (giorni)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={validitaGiorni}
                      onChange={(e) => setValiditaGiorni(e.target.value)}
                      data-testid="validita-giorni"
                    />
                  </div>
                </div>

                {/* Specialisti Selection */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-cyan-600" />
                    Specialisti Assegnati
                  </Label>
                  {specialistiOptions.length > 0 ? (
                    <div className="border border-slate-200 rounded-lg p-3 space-y-2">
                      <p className="text-sm text-slate-500 mb-2">Seleziona gli specialisti che lavoreranno a questo progetto:</p>
                      <div className="flex flex-wrap gap-2">
                        {specialistiOptions.map((specialista) => (
                          <div
                            key={specialista.id}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
                              selectedSpecialisti.includes(specialista.id)
                                ? 'border-cyan-500 bg-cyan-50'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                            onClick={() => {
                              if (selectedSpecialisti.includes(specialista.id)) {
                                setSelectedSpecialisti(selectedSpecialisti.filter(id => id !== specialista.id));
                              } else {
                                setSelectedSpecialisti([...selectedSpecialisti, specialista.id]);
                              }
                            }}
                            data-testid={`specialista-option-${specialista.id}`}
                          >
                            <Checkbox
                              checked={selectedSpecialisti.includes(specialista.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedSpecialisti([...selectedSpecialisti, specialista.id]);
                                } else {
                                  setSelectedSpecialisti(selectedSpecialisti.filter(id => id !== specialista.id));
                                }
                              }}
                              className="h-4 w-4"
                            />
                            <span className="font-medium text-slate-900 text-sm">{specialista.specialista || specialista.nome}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500 p-3 bg-slate-50 rounded-lg border border-dashed">
                      Nessuno specialista configurato. Vai su Impostazioni per aggiungerli.
                    </div>
                  )}
                </div>

                {/* Tempi di Realizzazione */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-teal-600" />
                    Tempi di Realizzazione
                  </Label>
                  <Textarea
                    value={tempiRealizzazione}
                    onChange={(e) => setTempiRealizzazione(e.target.value)}
                    placeholder="es. La realizzazione del progetto richiederà circa 30 giorni lavorativi..."
                    rows={3}
                    className="resize-none"
                  />
                </div>

                {/* Materiale Richiesto al Cliente */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-teal-600" />
                    Materiale Richiesto al Cliente
                  </Label>
                  <Textarea
                    value={materialeCliente}
                    onChange={(e) => setMaterialeCliente(e.target.value)}
                    placeholder="es. Per procedere con il progetto, il cliente dovrà fornire..."
                    rows={3}
                    className="resize-none"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Note</Label>
                  <RichTextEditor
                    value={note}
                    onChange={setNote}
                    placeholder="Note aggiuntive per il preventivo..."
                    height="120px"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Summary Card */}
            <Card className="bg-gradient-to-br from-slate-800 to-slate-900 text-white border-0">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-300 text-sm">Totale Preventivo</p>
                    <p className="text-3xl font-heading font-bold mt-1">
                      {formatCurrency(totaleScontato)}
                    </p>
                    <p className="text-slate-400 text-sm mt-1">+ IVA</p>
                  </div>
                  {sconto > 0 && (
                    <div className="text-right">
                      <p className="text-slate-300 text-sm">Sconto</p>
                      <p className="text-xl font-semibold text-emerald-400 mt-1">
                        -{formatCurrency(sconto)}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4">
          <Button
            variant="outline"
            onClick={() => step > 1 ? setStep(step - 1) : navigate('/preventivi')}
            disabled={saving}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {step === 1 ? 'Annulla' : 'Indietro'}
          </Button>
          {step < 3 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={(step === 1 && !selectedCliente) || (step === 2 && selectedServizi.length === 0)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="next-step-btn"
            >
              Avanti
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="create-preventivo-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creazione...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Crea Preventivo
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default NuovoPreventivo;
